/**
 * Recharge l'application une fois lorsqu'un chunk lazy (CSS/JS) ne peut plus être préchargé —
 * typiquement un onglet resté ouvert pendant un déploiement, qui référence des hash de bundle
 * désormais absents du CDN. C'est la 2e cause de crash front en prod (≈ 12 % des [ErrorBoundary] :
 * « Unable to preload CSS… » / « Failed to fetch dynamically imported module… »).
 *
 * Garde anti-boucle : on ne recharge pas si un reload a déjà eu lieu il y a moins de
 * RELOAD_DEBOUNCE_MS. Un chargement nominal réarme naturellement la garde (l'horodatage vieillit),
 * donc un futur déploiement déclenchera bien un nouveau reload.
 */
const RELOAD_GUARD_KEY = 'rm:chunk-reload-at';
const RELOAD_DEBOUNCE_MS = 10_000;

export function installChunkReloadOnPreloadError(): void {
    window.addEventListener('vite:preloadError', (event) => {
        // L'accès au sessionStorage peut throw (Safari navigation privée, iframe sandboxée). On
        // l'isole pour ne JAMAIS empêcher le reload : c'est notre seule porte de sortie, sinon
        // l'utilisateur reste bloqué sur la route au chunk manquant.
        let lastReloadAt = 0;
        try {
            lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
        } catch {
            lastReloadAt = 0;
        }

        if (Date.now() - lastReloadAt < RELOAD_DEBOUNCE_MS) {
            // Déjà rechargé récemment et le chunk manque toujours : on laisse l'erreur remonter à
            // l'ErrorBoundary plutôt que de boucler sur des reloads.
            return;
        }

        try {
            sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
        } catch {
            // Storage indisponible : pas de garde anti-boucle, mais on recharge quand même.
        }

        event.preventDefault();
        window.location.reload();
    });
}
